import { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { fetchPage } from '../lib/cms';
import GalleryLightbox from '../components/GalleryLightbox';
import ErrorPage from './ErrorPage';
import { Zap, Flame, Shield, Cpu, ExternalLink, Award, ChevronRight } from 'lucide-react';

export default function PageDetail({ defaultSlug }) {
  const { subpage } = useParams();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Resolve slug from route parameters or defaultSlug
  const slug = subpage || defaultSlug || location.pathname.split('/').pop() || 'index';

  useEffect(() => {
    async function loadPage() {
      setLoading(true);
      const data = await fetchPage(slug);
      setPage(data);
      setLoading(false);
    }
    loadPage();
  }, [slug]);

  // Update SEO metadata dynamically
  useEffect(() => {
    if (!page) return;

    const titleText = page.title?.da || page.title || `${slug.charAt(0).toUpperCase() + slug.slice(1)} - D.E. TekniQ K/S`;
    document.title = titleText;

    const metaDesc = page.meta_description?.da || page.meta_description || '';
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute('name', 'description');
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDesc);
  }, [page, slug]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-navy-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!page) {
    return <ErrorPage type="404" />;
  }

  // Determine icon based on page category/slug
  const getHeroIcon = () => {
    if (slug.includes('varmepumpe') || slug.includes('daikin') || slug.includes('luft-til-luft') || slug.includes('service') || slug.includes('tilskud')) {
      return <Flame className="h-8 w-8 text-energy-500" />;
    }
    if (slug.includes('el-') || slug.includes('ladebokse') || slug.includes('ihc') || slug.includes('smart')) {
      return <Zap className="h-8 w-8 text-navy-500" />;
    }
    return <Shield className="h-8 w-8 text-red-500" />;
  };

  // Check if we should render a banner for Varmepumpepuljen link
  const isTilskudPage = slug === 'tilskud-og-finansiering';

  // Sidebar categories navigation
  const categories = [
    {
      title: 'Varmepumper',
      links: [
        { label: 'Daikin Luft til Vand', href: '/varmepumper/daikin-luft-til-vand', slug: 'daikin-luft-til-vand' },
        { label: 'Luft til Luft Varmepumper', href: '/varmepumper/luft-til-luft', slug: 'luft-til-luft' },
        { label: 'Service & Vedligehold', href: '/varmepumper/service-og-vedligehold', slug: 'service-og-vedligehold' },
        { label: 'Tilskud & Finansiering', href: '/varmepumper/tilskud-og-finansiering', slug: 'tilskud-og-finansiering' },
      ]
    },
    {
      title: 'El-installation',
      links: [
        { label: 'Traditionelt El-arbejde', href: '/el-installation/traditionelt-el-arbejde', slug: 'traditionelt-el-arbejde' },
        { label: 'Ladebokse til Elbil', href: '/el-installation/ladebokse-til-elbil', slug: 'ladebokse-til-elbil' },
        { label: 'IHC & Smart Home', href: '/el-installation/ihc-og-smart-home', slug: 'ihc-og-smart-home' },
      ]
    },
    {
      title: 'Sikring & Netværk',
      links: [
        { label: 'Alarmsystemer & Overvågning', href: '/sikring-og-netvaerk/alarmsystemer-og-overvaagning', slug: 'alarmsystemer-og-overvaagning' },
        { label: 'Teledata & Netværk', href: '/sikring-og-netvaerk/teledata-og-netvaerk', slug: 'teledata-og-netvaerk' },
      ]
    }
  ];

  const activeCategory = categories.find(cat => cat.links.some(link => link.slug === slug));

  return (
    <div className="bg-white">
      {/* Page Header Banner */}
      <div className="bg-navy-950 py-16 text-white md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>
        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl">
            {page.title?.da || page.title}
          </h1>
          {page.meta_description?.da && (
            <p className="mx-auto mt-4 max-w-2xl text-lg text-navy-200">
              {page.meta_description.da}
            </p>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* Left Column: Rich Text Content */}
          <div className="lg:col-span-8">
            <article
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: page.content?.da || page.content }}
            />

            {/* Special Banner for Tilskud & Finansiering */}
            {isTilskudPage && (
              <div className="mt-8 rounded-none border border-energy-200 bg-energy-50/50 p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <Flame className="h-6 w-6 text-energy-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-navy-950">Varmepumpepuljen 2026 er åben!</h3>
                    <p className="text-sm text-graphite-700 mt-2">
                      Søg om tilskud til din nye luft til vand varmepumpe i dag. Reglerne er blevet enklere, og tilskuddet er højere. Husk, at installationen skal udføres af en VE-godkendt installatør som D.E. TekniQ K/S for at opnå tilskuddet.
                    </p>
                    <a
                      href="https://ens.dk/presse/varmepumpepuljen-2026-aabner-i-dag-med-enklere-regler-og-hoejere-tilskud"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-1.5 rounded-none bg-energy-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-energy-700 transition-colors"
                    >
                      Gå til Energistyrelsen <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Gallery Lightbox Integration removed */}
          </div>

          {/* Right Column: Sidebar / Contact Box */}
          <div className="lg:col-span-4">
            <div className="sticky lg:top-44 top-24 max-h-[calc(100vh-12rem)] overflow-y-auto space-y-6 pr-2 scrollbar-thin">
              {/* Contact Box */}
              <div className="rounded-none border border-navy-100 bg-navy-50/50 p-6 shadow-sm">
                <h3 className="text-lg font-bold text-navy-950">Brug for rådgivning?</h3>
                <p className="text-sm text-graphite-600 mt-2 leading-relaxed">
                  Vi står altid klar til at hjælpe dig med at finde den helt rigtige løsning til dit behov.
                </p>
                <div className="mt-6 space-y-4">
                  <a
                    href="tel:+4586667700"
                    className="flex items-center gap-3 rounded-none bg-white border border-navy-100 p-3 text-sm font-semibold text-navy-950 hover:border-energy-500 hover:text-energy-600 transition-all shadow-sm"
                  >
                    <span className="rounded-none bg-energy-50 p-2 text-energy-600">
                      <Zap className="h-4 w-4" />
                    </span>
                    <span className="whitespace-nowrap">+45 86 66 77 00</span>
                  </a>
                  <a
                    href="mailto:post@detekniq.dk"
                    className="flex items-center gap-3 rounded-none bg-white border border-navy-100 p-3 text-sm font-semibold text-navy-950 hover:border-energy-500 hover:text-energy-600 transition-all shadow-sm"
                  >
                    <span className="rounded-none bg-energy-50 p-2 text-energy-600">
                      <Zap className="h-4 w-4" />
                    </span>
                    <span className="whitespace-nowrap">post@detekniq.dk</span>
                  </a>
                </div>
              </div>

              {/* Trust Badge */}
              <div className="rounded-none bg-navy-900 p-6 text-white text-center">
                <Award className="h-8 w-8 text-energy-400 mx-auto mb-3" />
                <h4 className="font-bold">Autoriseret Garanti</h4>
                <p className="text-xs text-navy-200 mt-1 leading-relaxed">
                  Som medlem af TEKNIQ og ISO-certificeret installatør er du dækket af branchens stærkeste garantiordning.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
